import { TuiSkeleton } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TuiDropdown, TuiButton } from '@taiga-ui/core';
import { IsLoadingService } from '@core/services';
import { RequestService } from '@core/services/request/request.service';
import { RequestStatusesService } from '@core/services/request/request-statuses.service';
import { BehaviorSubject } from 'rxjs';
import { RequestStatus } from '@core/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { RequestFormStatusComponent } from '@features/requests';
import { hexToRgba } from '@core/helpers';

@Component({
  selector: 'x-request-form-change-status-button',
  imports: [
    TuiSkeleton,
    CommonModule,
    TuiButton,
    TranslatePipe,
    RequestFormStatusComponent,
    TuiDropdown,
  ],
  templateUrl: './request-form-change-status-button.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormChangeStatusButtonComponent implements OnDestroy {
  private readonly requestService = inject(RequestService);
  private readonly requestStatusesService = inject(RequestStatusesService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  readonly $request = this.requestService.$request;
  readonly $requestStatuses: Signal<RequestStatus[]> =
    this.requestStatusesService.$entities;

  $dropdownOpen = signal(false);
  $isMobile = input.required<boolean>({ alias: 'isMobile' });

  $statusChangeInProcess = signal(false);

  constructor() {
    effect(() => {
      if (this.$request()) {
        this.requestStatusesService.entities$
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      }
    });

    effect(() => {
      const dialogHost = this.document.querySelector(
        'tui-dialog-host',
      ) as HTMLElement;

      if (this.$dropdownOpen()) {
        setTimeout(() => this.addDocumentClickListener(), 600); // Delay to ensure dropdown opens
        dialogHost?.style.setProperty('z-index', '1');
      } else {
        dialogHost?.style.setProperty('z-index', 'unset');
        this.removeDocumentClickListener();
      }
    });
  }

  toggleDropdown(): void {
    this.$dropdownOpen.update(prev => !prev);
  }

  statusChangeInProcessChange(statusChangeInProcess: boolean): void {
    this.$statusChangeInProcess.set(statusChangeInProcess);
  }

  private backdropClickHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const statusesListElement = this.document.querySelector(
      'x-request-form-statuses-list',
    );

    if (
      this.$dropdownOpen() &&
      !this.$statusChangeInProcess() &&
      target !== statusesListElement
    ) {
      this.$dropdownOpen.set(false);
    }
  };

  addDocumentClickListener(): void {
    this.document.addEventListener('click', this.backdropClickHandler);
  }

  removeDocumentClickListener(): void {
    this.document.removeEventListener('click', this.backdropClickHandler);
  }

  protected readonly hexToRgba = hexToRgba;

  ngOnDestroy(): void {
    // Ensure document listener is removed if component is destroyed while dropdown is open
    this.removeDocumentClickListener();
  }
}
